import {sum} from "../util/util"
import {Entity, EntityType} from "./entity"
import {ObjectCostRateModelEntity} from "./object-cost-rate-model"

export abstract class CostedEntity implements Entity {

  readonly abstract displayName: string;
  readonly abstract entityType: EntityType;
  abstract id: string;
  abstract objectCostRateModels?: ObjectCostRateModelEntity[]

  private getObjectCostRateModels(rateModel: string | undefined = undefined): ObjectCostRateModelEntity[] {
    if (this.objectCostRateModels) {
      if (rateModel) {
        return this.objectCostRateModels.filter((ocrm) => ocrm.rateModelName === rateModel || ocrm.rateModelId === rateModel)
      } else {
        return this.objectCostRateModels
      }
    } else {
      return []
    }
  }

  getBillableHours(rateModel: string | undefined = undefined): number | undefined {
    return sum(this.getObjectCostRateModels(rateModel), (ocrm) => ocrm.billableHours)
  }

  getBillableQty(rateModel: string | undefined = undefined): number | undefined {
    return sum(this.getObjectCostRateModels(rateModel), (ocrm) => ocrm.billableQty)
  }

  getBillableFreightQty(rateModel: string | undefined = undefined): number | undefined {
    return sum(this.getObjectCostRateModels(rateModel), (ocrm) => ocrm.billableFreightQty)
  }

  getFuelSurchargeCost(rateModel: string | undefined = undefined): number | undefined {
    return sum(this.getObjectCostRateModels(rateModel), (ocrm) => ocrm.fuelSurchargeCost)
  }

  getDemmurageCost(rateModel: string | undefined = undefined): number | undefined {
    return sum(this.getObjectCostRateModels(rateModel), (ocrm) => ocrm.demurrageCost)
  }

  getRejectCost(rateModel: string | undefined = undefined): number | undefined {
    return sum(this.getObjectCostRateModels(rateModel), (ocrm) => ocrm.ticketRejectLoadCost)
  }

  getSplitLoadCost(rateModel: string | undefined = undefined): number | undefined {
    return sum(this.getObjectCostRateModels(rateModel), (ocrm) => ocrm.splitLoadCost)
  }

  getGenericTimeCost(rateModel: string | undefined = undefined): number | undefined {
    return sum(this.getObjectCostRateModels(rateModel), (ocrm) => ocrm.genericTimeCost)
  }

  getGenericFreightCost(rateModel: string | undefined = undefined): number | undefined {
    return sum(this.getObjectCostRateModels(rateModel), (ocrm) => ocrm.genericFreightCost)
  }

  getGenericLoadCost(rateModel: string | undefined = undefined): number | undefined {
    return sum(this.getObjectCostRateModels(rateModel), (ocrm) => ocrm.genericLoadCost)
  }

  getTimeCost(rateModel: string | undefined = undefined): number | undefined {
    return sum(this.getObjectCostRateModels(rateModel), (ocrm) => ocrm.timeCost)
  }

  getFreightCost(rateModel: string | undefined = undefined): number | undefined {
    return sum(this.getObjectCostRateModels(rateModel), (ocrm) => ocrm.freightCost)
  }

  getLoadCost(rateModel: string | undefined = undefined): number | undefined {
    return sum(this.getObjectCostRateModels(rateModel), (ocrm) => ocrm.loadCost)
  }

  getTotalCost(rateModel: string | undefined = undefined): number | undefined {
    return sum(this.getObjectCostRateModels(rateModel), (ocrm) => ocrm.totalCost)
  }

  get billableHours(): number | undefined {
    return this.getBillableHours()
  }

  get billableQty(): number | undefined {
    return this.getBillableQty()
  }

  get billableFreightQty(): number | undefined {
    return this.getBillableFreightQty()
  }

  get fuelSurchargeCost(): number | undefined {
    return this.getFuelSurchargeCost()
  }

  get demmurageCost(): number | undefined {
    return this.getDemmurageCost()
  }

  get rejectCost(): number | undefined {
    return this.getRejectCost()
  }

  get splitLoadCost(): number | undefined {
    return this.getSplitLoadCost()
  }

  get genericTimeCost(): number | undefined {
    return this.getGenericTimeCost()
  }

  get genericFreightCost(): number | undefined {
    return this.getGenericFreightCost()
  }

  get genericLoadCost(): number | undefined {
    return this.getGenericLoadCost()
  }

  get timeCost(): number | undefined {
    return this.getTimeCost()
  }

  get freightCost(): number | undefined {
    return this.getFreightCost()
  }

  get loadCost(): number | undefined {
    return this.getLoadCost()
  }

  get totalCost(): number | undefined {
    return this.getTotalCost()
  }
}
